/**
 * PWA plumbing (change add-pwa-install).
 *
 * - Unconditional SW registration (D-register): the push handlers of
 *   add-web-push stay intact; we register at boot so offline caching
 *   works without enabling push.
 * - Install prompt capture (D6): custom button in app-shell + iOS tip.
 * - Cache cleanup on logout / tenant switch (D8).
 */

const SW_URL = "/sw.js";

export async function registerServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    await navigator.serviceWorker.register(SW_URL, { type: "module" });
  } catch {
    // SW unavailable (insecure context, very old browser): the app keeps
    // working as a normal web app.
  }
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(available: boolean) => void>();

function notify(): void {
  for (const fn of listeners) fn(deferredPrompt !== null);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

/** Subscribe to install-prompt availability. Returns an unsubscribe fn. */
export function onInstallAvailable(fn: (available: boolean) => void): () => void {
  listeners.add(fn);
  fn(deferredPrompt !== null);
  return () => listeners.delete(fn);
}

/** Shows the captured native install prompt (no-op if unavailable). */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  const prompt = deferredPrompt;
  deferredPrompt = null;
  notify();
  await prompt.prompt();
  const choice = await prompt.userChoice;
  return choice.outcome;
}

/** True when running inside an installed standalone window. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * iOS Safari has no beforeinstallprompt: show the "Add to Home Screen"
 * tip instead (D6). Detected via UA string + touch support heuristics.
 */
export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && !isStandalone();
}

/**
 * fix-tenant-switch-isolation: tell the SW which tenant is active so its
 * whitelisted /api cache entries are namespaced per tenant. Called on
 * session restore, on login and on every tenant switch. A null tid means
 * "no tenant" — the SW stops caching /api until a tenant is pinned again.
 */
export function sendTenantToSw(tid: string | null): void {
  const sw = typeof navigator === "undefined" ? undefined : navigator.serviceWorker;
  if (!sw?.getRegistration) return;
  void sw
    .getRegistration()
    .then((reg) => {
      reg?.active?.postMessage({ type: "visera:tenant", tid });
    })
    .catch(() => undefined);
}

/**
 * D8: wipe every SW cache and the offline IndexedDB stores. Called on
 * logout and on tenant switch so no other tenant's/user's data remains
 * on disk.
 */
export async function clearPwaCaches(
  deps: { clearOfflineStores?: () => Promise<void> } = {},
): Promise<void> {
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith("visera-")).map((k) => caches.delete(k)));
    }
  } catch {
    // best-effort
  }
  try {
    await (deps.clearOfflineStores ?? (await import("@/lib/offline-store")).clearOfflineStores)();
  } catch {
    // best-effort
  }
}
